import { LOAI_DINH_MUC } from "@/core/constants/const";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
} from "@/core/helpers/number-format";
import type { IVatTuSelectedInKho } from "@/core/interfaces/Models";
import type { BbgtKhoItem } from "@/types/phieu-xuat-kho";
import { ref, type Ref } from "vue";
import type {
    IPhieuXuatKhoSuppliesQuantityRow,
    PhieuXuatKhoSuppliesSource,
} from "../types";
import { useSuppliesQuantityValidation } from "./useSuppliesQuantityValidation";

interface QuantityHandlersContext {
  source: Ref<PhieuXuatKhoSuppliesSource>;
  readonly: Ref<boolean>;
  isEditMode: Ref<boolean>;
  selectingKhoItem: Ref<BbgtKhoItem | null | undefined>;
  quantityRows: Ref<IPhieuXuatKhoSuppliesQuantityRow[]>;
  vatTuSelectedInKho: Ref<IVatTuSelectedInKho[]>;
  loaiDinhMuc: Ref<LOAI_DINH_MUC | undefined>;
  onQuantityChange: (rows: IPhieuXuatKhoSuppliesQuantityRow[]) => void;
}

export function usePhieuXuatKhoTableQuantityHandlers(
  ctx: QuantityHandlersContext
) {
  const {
    onExportQuantityChanged,
    onManualExportQuantityChanged,
    onMatChanged,
    onQuyDoiKgChanged,
    onSoLuongPheLieuChanged,
  } = useSuppliesQuantityValidation(ctx.loaiDinhMuc, (idVatTu) => {
    return (
      ctx.vatTuSelectedInKho.value.find(
        ({ id_vat_tu }) => id_vat_tu === idVatTu
      )?.con_thieu ?? 0
    );
  });

  const getQuantityRowByVatTuId = (idVatTu: number) =>
    ctx.quantityRows.value.find(({ id_vat_tu }) => id_vat_tu === idVatTu);

  const getQuantityMin: (idVatTu: number) => number = () => 0;

  const getDonGia = (idVatTu: number) =>
    getQuantityRowByVatTuId(idVatTu)?.don_gia_trong_kho ?? 0;

  const canEditQuantity = (idVatTu: number) => {
    if (ctx.readonly.value) {
      return false;
    }

    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return false;
    }

    if (ctx.source.value === "manual") {
      return true;
    }

    if (!ctx.selectingKhoItem.value) {
      return false;
    }

    return row.so_luong_trong_kho_ban_dau > 0;
  };

  const emitQuantityChange = () => {
    ctx.onQuantityChange([...ctx.quantityRows.value]);
  };

  const quantityBeforeEdit = ref<Record<number, number>>({});

  const captureQuantityBeforeEdit = (idVatTu: number) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (row) {
      quantityBeforeEdit.value[idVatTu] = row.so_luong_se_lay;
    }
  };

  const onQuantityFocus = (idVatTu: number, event: FocusEvent) => {
    captureQuantityBeforeEdit(idVatTu);
    (event.target as HTMLInputElement)?.select();
  };

  const onQuantityChange = async (idVatTu: number, value: unknown) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return;
    }

    const previousValue =
      quantityBeforeEdit.value[idVatTu] ?? row.so_luong_se_lay;

    if (ctx.source.value === "manual") {
      await onManualExportQuantityChanged(row, value);
    } else {
      await onExportQuantityChanged(row, value, previousValue);
    }

    emitQuantityChange();
  };

  const onQuantityBlur = (idVatTu: number) => {
    onQuantityChange(
      idVatTu,
      getQuantityRowByVatTuId(idVatTu)?.so_luong_se_lay
    );
  };

  const onMatChange = (idVatTu: number, value: unknown) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return;
    }

    onMatChanged(row, value);
    emitQuantityChange();
  };

  const onMatBlur = (idVatTu: number) => {
    onMatChange(idVatTu, getQuantityRowByVatTuId(idVatTu)?.mat);
  };

  const onPheLieuChange = (idVatTu: number, value: unknown) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return;
    }

    onSoLuongPheLieuChanged(row, value);
    emitQuantityChange();
  };

  const onPheLieuBlur = (idVatTu: number) => {
    onPheLieuChange(
      idVatTu,
      getQuantityRowByVatTuId(idVatTu)?.so_luong_phe_lieu
    );
  };

  const onQuyDoiChange = (idVatTu: number, value: unknown) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return;
    }

    onQuyDoiKgChanged(row, value);
    emitQuantityChange();
  };

  const onQuyDoiBlur = (idVatTu: number) => {
    onQuyDoiChange(idVatTu, getQuantityRowByVatTuId(idVatTu)?.quy_doi_kg);
  };

  const calcRemainingNeed = (idVatTu: number) => {
    const row = getQuantityRowByVatTuId(idVatTu);
    const master = ctx.vatTuSelectedInKho.value.find(
      ({ id_vat_tu }) => id_vat_tu === idVatTu
    );

    if (ctx.selectingKhoItem.value && row?.chenh_lech != null) {
      return formatQuantity(row.chenh_lech);
    }

    if (master) {
      return formatQuantity(master.con_thieu);
    }

    return formatQuantity(row?.so_luong_can ?? 0);
  };

  const thanhTienCalc = (idVatTu: number) => {
    const row = getQuantityRowByVatTuId(idVatTu);

    if (!row) {
      return formatAmount(0);
    }

    return formatAmount(
      calcLineAmount(getDonGia(idVatTu), row.so_luong_se_lay)
    );
  };

  return {
    calcRemainingNeed,
    canEditQuantity,
    emitQuantityChange,
    getDonGia,
    getQuantityMin,
    getQuantityRowByVatTuId,
    onMatBlur,
    onMatChange,
    onPheLieuBlur,
    onPheLieuChange,
    onQuantityBlur,
    onQuantityChange,
    onQuantityFocus,
    onQuyDoiBlur,
    onQuyDoiChange,
    thanhTienCalc,
  };
}
